import type {
  EvergreenMp4ReplacementType,
  EvergreenVideoCut,
  EvergreenScheduledInteractionType,
  EvergreenTimelineEvent,
} from '@/features/webinar/types';
import {
  isHideEvent,
  resolveDisplayEventType,
  sortTimelineEvents,
} from '@/features/webinar/evergreenTimelineUtils';
import { isSchedulableInteractionType } from '@/lib/evergreenInteractionSchedule';

/** Passage retiré de la vidéo, exprimé en temps de l'ancienne vidéo. */
export type VideoCut = EvergreenVideoCut;

export type VideoCutsError =
  | 'NO_CUT'
  | 'INVALID_CUT'
  | 'DURATION_UNKNOWN'
  | 'CUT_OUTSIDE_VIDEO'
  | 'CUTS_OVERLAP'
  | 'TOTAL_MISMATCH';

export const VIDEO_CUTS_ERRORS: readonly VideoCutsError[] = [
  'NO_CUT',
  'INVALID_CUT',
  'DURATION_UNKNOWN',
  'CUT_OUTSIDE_VIDEO',
  'CUTS_OVERLAP',
  'TOTAL_MISMATCH',
];

/** Écart toléré entre le total des coupures et la différence de durée des deux vidéos. */
export const CUTS_TOTAL_TOLERANCE_SECONDS = 2;

function sortCuts(cuts: readonly VideoCut[]): VideoCut[] {
  return [...cuts].sort((a, b) => a.startSeconds - b.startSeconds);
}

/**
 * Une coupure peut commencer à 00:00:00 ou finir pile à la fin de l'ancienne vidéo. La nouvelle
 * durée est celle lue par le navigateur, d'où la tolérance sur le total.
 */
export function validateVideoCuts(
  cuts: readonly VideoCut[],
  previousDurationSeconds: number | null,
  newDurationSeconds: number | null,
): VideoCutsError | null {
  if (cuts.length === 0) return 'NO_CUT';
  if (cuts.some((cut) => cut.startSeconds < 0 || cut.durationSeconds <= 0)) return 'INVALID_CUT';
  if (previousDurationSeconds == null || newDurationSeconds == null) return 'DURATION_UNKNOWN';
  const sorted = sortCuts(cuts);
  if (sorted.some((cut) => cut.startSeconds + cut.durationSeconds > previousDurationSeconds)) {
    return 'CUT_OUTSIDE_VIDEO';
  }
  for (let index = 1; index < sorted.length; index++) {
    const previous = sorted[index - 1];
    if (sorted[index].startSeconds < previous.startSeconds + previous.durationSeconds) {
      return 'CUTS_OVERLAP';
    }
  }
  const totalCut = sorted.reduce((sum, cut) => sum + cut.durationSeconds, 0);
  const durationGap = previousDurationSeconds - newDurationSeconds;
  return Math.abs(totalCut - durationGap) > CUTS_TOTAL_TOLERANCE_SECONDS ? 'TOTAL_MISMATCH' : null;
}

export type TooShortHint = 'DECLARE_CUTS' | 'CHECK_CUTS' | 'NEW_VIDEO';

/**
 * Sans coupure déclarée, la vidéo a peut-être été raccourcie : on propose de déclarer les coupures.
 * Une vidéo indépendante n'a pas de coupure à déclarer. Tant que les coupures saisies ne sont pas
 * exploitables, leur propre erreur suffit.
 */
export function resolveTooShortHint({
  blocked,
  replacementType,
  cutsApplied,
}: {
  blocked: boolean;
  replacementType: EvergreenMp4ReplacementType | null;
  cutsApplied: boolean;
}): TooShortHint | null {
  if (!blocked) return null;
  switch (replacementType) {
    case 'INDEPENDENT_VIDEO':
      return 'NEW_VIDEO';
    case 'PREVIOUS_VIDEO_WITH_CUTS':
      return cutsApplied ? 'CHECK_CUTS' : null;
    case 'PREVIOUS_VIDEO_UNCUT':
    case null:
      return 'DECLARE_CUTS';
  }
}

/** Une coupure couvre `[début, début + durée[` : un instant pile à sa fin est conservé. */
export function isInVideoCut(offsetSeconds: number, cuts: readonly VideoCut[]): boolean {
  return cuts.some(
    (cut) =>
      offsetSeconds >= cut.startSeconds && offsetSeconds < cut.startSeconds + cut.durationSeconds,
  );
}

/**
 * Recule un instant de tout ce qui a été coupé avant lui. Un instant situé dans une coupure revient
 * au point de coupure.
 */
export function shiftOffsetForCuts(offsetSeconds: number, cuts: readonly VideoCut[]): number {
  return cuts.reduce(
    (shifted, cut) =>
      shifted - Math.min(Math.max(offsetSeconds - cut.startSeconds, 0), cut.durationSeconds),
    offsetSeconds,
  );
}

export type VideoCutsDeletedItem =
  | { kind: 'MESSAGE'; eventId: number; offsetSeconds: number; text: string }
  | {
      kind: 'INTERACTION';
      eventId: number;
      type: EvergreenScheduledInteractionType;
      resourceId: number | null;
      startOffsetSeconds: number;
      endOffsetSeconds: number | null;
    }
  | { kind: 'LIVE_REACTION'; eventId: number; offsetSeconds: number };

export type VideoCutsApplication = {
  /** Timeline recalée, sans les éléments supprimés. */
  timeline: EvergreenTimelineEvent[];
  shiftedCount: number;
  deleted: VideoCutsDeletedItem[];
};

const MESSAGE_LINKED_TYPES: readonly string[] = ['CHAT_REACTION', 'MESSAGE_PIN', 'MESSAGE_UNPIN'];

export function getSupportMessageText(event: EvergreenTimelineEvent): string {
  if (!event.payloadJson) return event.title ?? '';
  try {
    const parsed: unknown = JSON.parse(event.payloadJson);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object' && 'content' in parsed) {
      return typeof parsed.content === 'string' ? parsed.content : (event.title ?? '');
    }
    return event.title ?? '';
  } catch {
    return event.payloadJson;
  }
}

type InteractionWindow = {
  type: EvergreenScheduledInteractionType;
  show: EvergreenTimelineEvent;
  hide: EvergreenTimelineEvent | null;
  endSeconds: number | null;
};

/** Même appariement que l'éditeur de créneaux : chaque masquage ferme la plus ancienne apparition. */
function collectInteractionWindows(
  events: readonly EvergreenTimelineEvent[],
  previousDurationSeconds: number | null,
): InteractionWindow[] {
  const windows: InteractionWindow[] = [];
  const openShows = new Map<
    string,
    { type: EvergreenScheduledInteractionType; shows: EvergreenTimelineEvent[] }
  >();
  for (const event of events) {
    const type = resolveDisplayEventType(event);
    if (!isSchedulableInteractionType(type)) continue;
    const key = `${type}-${event.resourceId}`;
    if (event.endOffsetSeconds != null) {
      windows.push({ type, show: event, hide: null, endSeconds: event.endOffsetSeconds });
    } else if (isHideEvent(event)) {
      const show = openShows.get(key)?.shows.shift();
      if (show) windows.push({ type, show, hide: event, endSeconds: event.startOffsetSeconds });
    } else {
      const group = openShows.get(key) ?? { type, shows: [] };
      group.shows.push(event);
      openShows.set(key, group);
    }
  }
  // Une apparition jamais fermée court jusqu'à la fin de l'ancienne vidéo.
  for (const { type, shows } of openShows.values()) {
    for (const show of shows) {
      windows.push({ type, show, hide: null, endSeconds: previousDurationSeconds });
    }
  }
  return windows;
}

/**
 * Applique les coupures à la timeline, comme le back le fera à la promotion de la vidéo. Les
 * messages coupés partent avec leurs épinglages et réactions ; les réactions emoji du Live coupées
 * aussi ; un créneau réduit à rien est supprimé ; tout le reste recule.
 */
export function applyVideoCuts(
  timeline: readonly EvergreenTimelineEvent[],
  cuts: readonly VideoCut[],
  previousDurationSeconds: number | null,
): VideoCutsApplication {
  const sortedCuts = sortCuts(cuts);
  const shift = (offsetSeconds: number) => shiftOffsetForCuts(offsetSeconds, sortedCuts);
  const events = sortTimelineEvents([...timeline]);
  const deletedIds = new Set<number>();
  const deleted: VideoCutsDeletedItem[] = [];

  const deletedMessageIds = new Set<number>();
  for (const event of events) {
    if (
      event.eventType === 'SUPPORT_MESSAGE' &&
      isInVideoCut(event.startOffsetSeconds, sortedCuts)
    ) {
      deletedMessageIds.add(event.id);
      deletedIds.add(event.id);
      deleted.push({
        kind: 'MESSAGE',
        eventId: event.id,
        offsetSeconds: event.startOffsetSeconds,
        text: getSupportMessageText(event),
      });
    }
  }
  for (const event of events) {
    const linkedToDeletedMessage =
      MESSAGE_LINKED_TYPES.includes(event.eventType) &&
      event.resourceId != null &&
      deletedMessageIds.has(event.resourceId);
    if (linkedToDeletedMessage) deletedIds.add(event.id);
    if (event.eventType === 'LIVE_REACTION' && isInVideoCut(event.startOffsetSeconds, sortedCuts)) {
      deletedIds.add(event.id);
      deleted.push({
        kind: 'LIVE_REACTION',
        eventId: event.id,
        offsetSeconds: event.startOffsetSeconds,
      });
    }
  }

  const pairedHideByShowId = new Map<number, EvergreenTimelineEvent>();
  for (const window of collectInteractionWindows(events, previousDurationSeconds)) {
    if (window.hide) pairedHideByShowId.set(window.show.id, window.hide);
    if (window.endSeconds == null) continue;
    if (shift(window.endSeconds) > shift(window.show.startOffsetSeconds)) continue;
    deletedIds.add(window.show.id);
    if (window.hide) deletedIds.add(window.hide.id);
    deleted.push({
      kind: 'INTERACTION',
      eventId: window.show.id,
      type: window.type,
      resourceId: window.show.resourceId,
      startOffsetSeconds: window.show.startOffsetSeconds,
      endOffsetSeconds: window.endSeconds,
    });
  }

  const pairedHideIds = new Set([...pairedHideByShowId.values()].map((hide) => hide.id));
  const isShifted = (event: EvergreenTimelineEvent) =>
    shift(event.startOffsetSeconds) !== event.startOffsetSeconds ||
    (event.endOffsetSeconds != null && shift(event.endOffsetSeconds) !== event.endOffsetSeconds);

  let shiftedCount = 0;
  const shiftedTimeline: EvergreenTimelineEvent[] = [];
  for (const event of events) {
    if (deletedIds.has(event.id)) continue;
    shiftedTimeline.push({
      ...event,
      startOffsetSeconds: shift(event.startOffsetSeconds),
      endOffsetSeconds: event.endOffsetSeconds == null ? null : shift(event.endOffsetSeconds),
    });
    // La fin de session est recalculée sur la nouvelle durée, et un masquage compte avec son apparition.
    if (event.eventType === 'SESSION_FINISH' || pairedHideIds.has(event.id)) continue;
    const hide = pairedHideByShowId.get(event.id);
    if (isShifted(event) || (hide != null && isShifted(hide))) shiftedCount++;
  }

  return { timeline: shiftedTimeline, shiftedCount, deleted };
}
